import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import {
  addPlatformAdmin,
  budgetAllocationsQuery,
  platformAdminsQuery,
  removeAiPerson,
  removePlatformAdmin,
  saveAdamAccess,
  saveAiPerson,
  searchAiPeople,
  type AdamAccessRow,
  type AiAgentShell,
  type AiPerson,
  type BudgetAllocation,
  type PlatformAdmin,
  type PlatformAdminsPage,
} from '../../api/settingsAiAgent'
import { Spinner } from '../../ui/Spinner'
import { AllocationCells } from './AllocationCells'
import { failed, NoteText, type Note } from './parts'

/**
 * Fila "People & access" (Global, numai pentru un administrator de platforma -
 * poarta e pe server, fila doar nu se deseneaza altora). Pana pe 29 octombrie
 * 2026 erau doua file, "People" si "Platform admins & Adam access", care
 * spuneau fiecare jumatate despre acelasi om; acum e un rand pe om.
 *
 * DOUA LUCRURI IN ACELASI LOC, fiindca se dau la fel de repede si de aceiasi
 * oameni:
 *
 *  - lista `it_platform_admins` - cine publica agentul, scrie cheia AI a
 *    instantei, deschide Incident / Full Access. Pana pe 15 septembrie 2026 era
 *    scrisa in cod (`AgentReleasePublishers`), si o adresa noua cerea o livrare;
 *  - treptele lui Adam PE OM (`adam_intop.access`, `adam_intop.models`), scrise
 *    exact unde le scrie fisa omului din User Accounts > Roles - cele doua
 *    ecrane arata acelasi lucru, oricare ar fi scris ultimul. Langa ele, pe
 *    acelasi rand: firmele pe care are voie omul (`it_ai_users`, ce era fila
 *    People - din pagina AI Agent, `shell`), cu pasii pe intrebare si "On".
 *    "Tokens / day" nu mai e in ecran: alocarea in USD il inlocuieste.
 *
 * Fara `shell` (probele vechi) tabelul are numai oamenii lui Adam.
 */
export function PlatformAdminsTab({ shell = null, onShellChanged }: { shell?: AiAgentShell | null; onShellChanged?(): void } = {}) {
  const client = useQueryClient()
  const query = useQuery(platformAdminsQuery())
  const page = query.data ?? null

  if (query.isError) {
    return <p className="text-danger">{failed(query.error)}</p>
  }
  if (!page) return <Spinner />

  const reload = () => {
    void client.invalidateQueries({ queryKey: ['settings', 'ai_agent', 'platform_admins'] })
  }

  return (
    <div className="aia-platform-admins">
      <AdminsPanel page={page} onChanged={reload} />
      <AdamPanel page={page} shell={shell} onChanged={reload} onShellChanged={onShellChanged ?? reload} />
    </div>
  )
}

/* ------------------------------------------------------------------ *
 *  ADMINISTRATORII
 * ------------------------------------------------------------------ */

function AdminsPanel({ page, onChanged }: { page: PlatformAdminsPage; onChanged(): void }) {
  const [email, setEmail] = useState('')
  const [comment, setComment] = useState('')
  /* Cauta in conturi pe masura ce scrii (nume sau e-mail), ca la Adam access;
     alegerea pune adresa contului in camp. Dupa o alegere nu mai cauta. */
  const [found, setFound] = useState<Found[]>([])
  const [open, setOpen] = useState(false)
  const [picked, setPicked] = useState(false)
  const searchTimer = useRef<number | null>(null)

  useEffect(() => {
    if (searchTimer.current) window.clearTimeout(searchTimer.current)
    if (email.trim().length < 2 || picked) {
      setFound([])
      setOpen(false)
      return
    }
    searchTimer.current = window.setTimeout(() => {
      void searchAiPeople(email)
        .then((r) => {
          const withMail = r.people.filter((p) => p.email !== '')
          setFound(withMail)
          setOpen(withMail.length > 0)
        })
        .catch(() => setFound([]))
    }, 250)
    return () => {
      if (searchTimer.current) window.clearTimeout(searchTimer.current)
    }
  }, [email, picked])
  const [note, setNote] = useState<Note | null>(null)

  const add = () => {
    setNote({ text: '…' })
    void addPlatformAdmin(email.trim(), comment.trim())
      .then((r) => {
        if (!r.ok) {
          setNote({ text: r.error ?? 'Failed', bad: true })
          return
        }
        setNote({ text: 'Added' })
        setEmail('')
        setComment('')
        onChanged()
      })
      .catch((e: unknown) => setNote({ text: failed(e), bad: true }))
  }

  const activeInTable = page.admins.filter((a) => a.id > 0).length

  return (
    <div className="panel panel-default">
      <div className="panel-heading">
        <strong>Platform admins</strong>
        <span className="text-muted" style={{ fontSize: 12, marginLeft: 8 }}>
          publish the agent, set the instance AI key, open Incident and Full Access. Not a role right: a company admin
          is not one.
        </span>
      </div>
      <div className="panel-body">
        {!page.ready ? (
          <p className="text-danger" style={{ fontSize: 13 }}>
            The <code>it_platform_admins</code> table is missing, so the list written in code answers and nothing can be
            changed here. Run <code>php bin/console od:schema:apply --yes</code> on the server.
          </p>
        ) : page.fallback ? (
          <p className="text-warning" style={{ fontSize: 13 }}>
            The table has nobody active, so the list written in code answers. The first person you add keeps everyone
            below on the list.
          </p>
        ) : null}

        <table className="table table-condensed" style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>E-mail</th>
              <th>Added by</th>
              <th>Added</th>
              <th>Note</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {page.admins.map((admin) => (
              <AdminRow
                key={admin.id > 0 ? admin.id : admin.email}
                admin={admin}
                isMe={admin.email === page.me}
                canRemove={admin.id > 0 && activeInTable > 1}
                onRemoved={onChanged}
              />
            ))}
          </tbody>
        </table>

        {page.ready ? (
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', flex: '1 1 240px' }}>
              <label htmlFor="aia-admin-email" style={{ fontSize: 12, margin: 0 }}>
                Existing account
              </label>
              <input
                id="aia-admin-email"
                type="text"
                className="form-control input-sm"
                placeholder="type a name or e-mail"
                autoComplete="off"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  setPicked(false)
                }}
                onBlur={() => window.setTimeout(() => setOpen(false), 150)}
              />
              {open ? (
                <div
                  className="aia-admin-found"
                  style={{
                    position: 'absolute',
                    zIndex: 20,
                    background: '#fff',
                    border: '1px solid #d5dbe1',
                    borderRadius: 6,
                    maxHeight: 220,
                    overflow: 'auto',
                    width: '100%',
                    boxShadow: '0 6px 18px rgba(0,0,0,.08)',
                  }}
                >
                  {found.map((p) => (
                    <a
                      key={p.id}
                      href="#"
                      style={{ display: 'block', padding: '5px 10px', fontSize: 13, color: '#1f2933' }}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={(e) => {
                        e.preventDefault()
                        setEmail(p.email)
                        setPicked(true)
                        setOpen(false)
                      }}
                    >
                      {personName(p)}
                      <span style={{ color: '#8a939c' }}> · {p.email}</span>
                    </a>
                  ))}
                </div>
              ) : null}
            </div>
            <div style={{ flex: '1 1 200px' }}>
              <label htmlFor="aia-admin-note" style={{ fontSize: 12, margin: 0 }}>
                Why (optional)
              </label>
              <input
                id="aia-admin-note"
                type="text"
                className="form-control input-sm"
                maxLength={255}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </div>
            <button type="button" className="btn btn-primary btn-sm" disabled={email.trim() === ''} onClick={add}>
              Add platform admin
            </button>
            <NoteText note={note} />
          </div>
        ) : null}
        <p className="text-muted" style={{ fontSize: 12, margin: '8px 0 0' }}>
          Every add and remove is written to the event journal. The last admin cannot be removed.
        </p>
      </div>
    </div>
  )
}

function AdminRow({
  admin,
  isMe,
  canRemove,
  onRemoved,
}: {
  admin: PlatformAdmin
  isMe: boolean
  canRemove: boolean
  onRemoved(): void
}) {
  const [note, setNote] = useState<Note | null>(null)

  const remove = () => {
    const question = isMe
      ? 'Remove YOUR OWN address? You lose access to this screen immediately.'
      : `Remove ${admin.email} from the platform admins?`
    if (!window.confirm(question)) return

    setNote({ text: '…' })
    void removePlatformAdmin(admin.id)
      .then((r) => (r.ok ? onRemoved() : setNote({ text: r.error ?? 'Could not remove', bad: true })))
      .catch((e: unknown) => setNote({ text: failed(e), bad: true }))
  }

  return (
    <tr>
      <td>
        {admin.email}
        {isMe ? <span className="text-muted"> (you)</span> : null}
      </td>
      <td>{admin.id === 0 ? <em>code list</em> : admin.added_by_name || (admin.added_by > 0 ? '#' + admin.added_by : '—')}</td>
      <td>{admin.added_at || '—'}</td>
      <td>{admin.note}</td>
      <td style={{ whiteSpace: 'nowrap' }}>
        {admin.id > 0 ? (
          <button type="button" className="btn btn-default btn-xs" disabled={!canRemove} onClick={remove}>
            Remove
          </button>
        ) : null}{' '}
        <NoteText note={note} />
      </td>
    </tr>
  )
}

/* ------------------------------------------------------------------ *
 *  PEOPLE & ACCESS: un rand pe om
 * ------------------------------------------------------------------ */

/**
 * Omul din tabelul unit: treptele lui Adam (`adam`, din `platform_admins`) si
 * randurile lui din `it_ai_users` (`entries`, din pagina AI Agent) - firmele pe
 * care are voie, cu pasii pe intrebare si "On" ai fiecarei firme.
 */
type Person = {
  user_id: number
  name: string
  email: string
  adam: AdamAccessRow
  /** Nu e in lista de acces a lui Adam: vine numai din People (it_ai_users). */
  peopleOnly: boolean
  entries: AiPerson[]
}

/** Prenume Nume, din bucatile pe care le trimite serverul. */
function peopleName(p: AiPerson): string {
  const both = `${p.first ?? ''} ${p.last ?? ''}`.trim()
  return both !== '' ? both : p.name
}

/** Toti cei din Adam access + toti cei din People, o data fiecare, in ordinea asta. */
function mergePeople(adam: AdamAccessRow[], people: AiPerson[]): Person[] {
  const byUser = new Map<number, Person>()
  for (const row of adam) {
    byUser.set(row.user_id, { user_id: row.user_id, name: row.name, email: row.email, adam: row, peopleOnly: false, entries: [] })
  }
  for (const p of people) {
    let person = byUser.get(p.user_id)
    if (!person) {
      const name = peopleName(p)
      person = {
        user_id: p.user_id,
        name,
        email: p.email,
        adam: { user_id: p.user_id, name, email: p.email, access: 0, models: 0, role_access: 0, role_models: 0, parallel: 1, sources: [] },
        peopleOnly: true,
        entries: [],
      }
      byUser.set(p.user_id, person)
    }
    person.entries.push(p)
  }

  return [...byUser.values()]
}

function AdamPanel({
  page,
  shell,
  onChanged,
  onShellChanged,
}: {
  page: PlatformAdminsPage
  shell: AiAgentShell | null
  onChanged(): void
  onShellChanged(): void
}) {
  const peopleReady = shell?.peopleReady ?? false
  const persons = mergePeople(page.adam, peopleReady ? (shell?.people ?? []) : [])
  /* Alocarea lunara a fiecarui om din lista (bugetele lui Adam): o cerere
     pentru toti, langa accesul lor. Pica sau lipseste = celulele spun "-". */
  const allocations = useQuery(budgetAllocationsQuery(persons.map((p) => p.user_id)))
  const byUser = new Map<number, BudgetAllocation>((allocations.data?.rows ?? []).map((r) => [r.user_id, r]))
  const noAccount = page.adminsWithoutAccount ?? []

  return (
    <div className="panel panel-default">
      <div className="panel-heading">
        <strong>People &amp; access</strong>
        <span className="text-muted" style={{ fontSize: 12, marginLeft: 8 }}>
          who may use Adam, on which companies, what they may let it do, how deep it may think and how much it may
          spend. The levels are the same as User Accounts › Roles on that person.
        </span>
      </div>
      <div className="panel-body">
        {!page.adamReady ? (
          <p className="text-muted" style={{ fontSize: 13, margin: 0 }}>
            The <code>adam_intop.access</code> / <code>adam_intop.models</code> resources are not in the database yet.
          </p>
        ) : (
          <>
            <AdamPick page={page} shell={shell} onSaved={onChanged} onPeopleSaved={onShellChanged} />
            {persons.length === 0 && noAccount.length === 0 ? (
              <p className="text-muted" style={{ fontSize: 13, margin: 0 }}>
                Nobody has Adam access - not on their own account, not from a role, not as a platform admin - and no
                company has people listed.
              </p>
            ) : (
              <table className="table table-condensed" style={{ fontSize: 12, margin: 0 }}>
                <thead>
                  <tr>
                    <th>Person</th>
                    <th>Access from</th>
                    <th title="The companies this person may use Adam on. A company with people listed lets only them (and the platform admins) talk to Adam there; nobody listed = by the module right.">
                      Companies
                    </th>
                    <th>May let Adam</th>
                    <th>Thinking depth</th>
                    <th title="How many Adam conversations this person may keep working at the same time">Adam in parallel</th>
                    <th></th>
                    <th>Monthly allowance</th>
                    <th>This month</th>
                    <th title="0 = inherit (the company's, then the global setting)">Steps per question</th>
                    <th>On</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {persons.map((person) => (
                    /* Cheia poarta si treptele: dupa o reincarcare (o treapta data
                       din "Set level", sau un administrator nou cu 0) randul isi
                       ia valorile din server, nu le tine pe cele din prima desenare. */
                    <PersonRows
                      key={`${person.user_id}:${person.adam.access}:${person.adam.models}:${person.adam.parallel ?? 1}`}
                      person={person}
                      page={page}
                      shell={shell}
                      peopleReady={peopleReady}
                      onSaved={onChanged}
                      onPeopleSaved={onShellChanged}
                      allocation={byUser.get(person.user_id) ?? null}
                      allocationsReady={allocations.data?.ready ?? false}
                    />
                  ))}
                  {noAccount.map((email) => (
                    <tr key={'no-account:' + email} data-testid={'no-account-' + email}>
                      <td>{email}</td>
                      <td>
                        <span className="label label-default">Platform admin</span>
                      </td>
                      <td colSpan={10} className="text-warning">
                        No user account has this e-mail address, so there is nothing to set here (levels, parallel,
                        allowance, companies). Add the account in User Accounts, or remove the address from the platform
                        admins.
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <p className="text-muted" style={{ fontSize: 12, margin: '8px 0 0' }}>
              Listed: everyone with Adam access - on their own account, from their role, or as a platform admin - and
              everyone on a company list. A person gets the higher of their own level and their role&apos;s. Companies:
              while a company has nobody listed, everyone with the module right can use Adam there; &quot;any
              company&quot; counts on every company. Steps per question: 0 = inherit (the company&apos;s, then the global
              setting). Adam in parallel: how many conversations the person may keep working at the same time (1 = one at
              a time, as before). Changes apply on their next request.
            </p>
          </>
        )}
      </div>
    </div>
  )
}

function LevelSelect({
  label,
  value,
  names,
  onChange,
}: {
  label: string
  value: number
  names: string[]
  onChange(next: number): void
}) {
  return (
    <select
      aria-label={label}
      className="form-control input-sm"
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
    >
      {names.map((name, level) => (
        <option key={level} value={level}>
          {name}
        </option>
      ))}
    </select>
  )
}

/**
 * Randurile unui om: cel dintai poarta tot ce e pe om (treptele, paralelul,
 * alocarea), intins peste firmele lui; fiecare firma din `it_ai_users` are
 * randul ei (firma, pasi, On, Save, scoatere), iar ultimul adauga o firma.
 */
function PersonRows({
  person,
  page,
  shell,
  peopleReady,
  onSaved,
  onPeopleSaved,
  allocation,
  allocationsReady,
}: {
  person: Person
  page: PlatformAdminsPage
  shell: AiAgentShell | null
  peopleReady: boolean
  onSaved(): void
  onPeopleSaved(): void
  allocation: BudgetAllocation | null
  allocationsReady: boolean
}) {
  const row = person.adam
  const name = person.name || '#' + person.user_id
  const [access, setAccess] = useState(row.access)
  const [models, setModels] = useState(row.models)
  /* Implicit 1: fara setare, omul are un singur Adam in lucru, ca pana acum. */
  const [parallel, setParallel] = useState(row.parallel ?? 1)
  const [note, setNote] = useState<Note | null>(null)
  const parallelReady = page.parallelReady !== false
  const parallelMax = page.parallelMax ?? 5

  const save = () => {
    setNote({ text: '…' })
    /* Limita pleaca numai cand s-a schimbat: altfel nu se scrie niciun rand. */
    const changed = parallelReady && parallel !== (row.parallel ?? 1)
    void saveAdamAccess(row.user_id, access, models, changed ? parallel : undefined)
      .then((r) => {
        if (!r.ok) {
          setNote({ text: r.error ?? 'Failed', bad: true })
          return
        }
        setNote(r.warning ? { text: r.warning, bad: true } : { text: 'Saved' })
        onSaved()
      })
      .catch((e: unknown) => setNote({ text: failed(e), bad: true }))
  }

  const sources = row.sources ?? []
  const fromRole = [
    row.role_access > 0 ? page.actions[row.role_access] : '',
    row.role_models > 0 ? page.depths[row.role_models] : '',
  ].filter((x) => x !== '')

  /* Firmele lui, apoi randul de adaugat (numai cand tabela exista). */
  const span = person.entries.length + (peopleReady ? 1 : 0) || 1

  const personCells = (
    <>
      <td rowSpan={span} data-testid={'person-' + person.user_id}>
        {name}
        <div className="text-muted" style={{ fontSize: 11 }}>
          {person.email}
        </div>
      </td>
      <td rowSpan={span} style={{ fontSize: 11 }}>
        <div data-testid={'sources-' + person.user_id}>
          {sources.map((src) => (
            <span key={src} className="label label-default" style={{ marginRight: 4 }}>
              {SOURCE_LABEL[src]}
            </span>
          ))}
          {sources.length === 0 && person.peopleOnly ? <span className="label label-default">Company list</span> : null}
        </div>
        {fromRole.length ? (
          <div className="text-muted" data-testid={'role-' + person.user_id} style={{ marginTop: 2 }}>
            role: {fromRole.join(' / ')}
          </div>
        ) : null}
      </td>
    </>
  )

  const adamCells = (
    <>
      <td rowSpan={span} style={{ width: 170 }}>
        <LevelSelect label={`Adam access for ${name}`} value={access} names={page.actions} onChange={setAccess} />
      </td>
      <td rowSpan={span} style={{ width: 150 }}>
        <LevelSelect label={`Adam depth for ${name}`} value={models} names={page.depths} onChange={setModels} />
      </td>
      <td rowSpan={span} style={{ width: 90 }}>
        <select
          aria-label={`Adam in parallel for ${name}`}
          className="form-control input-sm"
          value={parallel}
          disabled={!parallelReady}
          title={parallelReady ? '' : 'Needs the it_ai_parallel_limits table - run the migrations.'}
          onChange={(e) => setParallel(Number(e.target.value))}
        >
          {Array.from({ length: parallelMax }, (_, i) => i + 1).map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </td>
      <td rowSpan={span} style={{ whiteSpace: 'nowrap' }}>
        <button type="button" className="btn btn-default btn-xs" onClick={save}>
          Save
        </button>{' '}
        <NoteText note={note} />
      </td>
      <AllocationCells name={name} allocation={allocation} ready={allocationsReady} rowSpan={span} />
    </>
  )

  /* Randul de adaugat; fara firme e chiar randul omului, cu celulele lui intre
     firma si pasi, ca in capul tabelului. */
  const addRow = (inner: ReactNode) =>
    peopleReady ? (
      <AddCompanyCells person={person} name={name} shell={shell} onAdded={onPeopleSaved}>
        {inner}
      </AddCompanyCells>
    ) : (
      <>
        <td className="text-muted">—</td>
        {inner}
        <td colSpan={3}></td>
      </>
    )

  if (person.entries.length === 0) {
    return (
      <tr>
        {personCells}
        {addRow(adamCells)}
      </tr>
    )
  }

  return (
    <>
      {person.entries.map((entry, i) => (
        <tr key={entry.ID}>
          {i === 0 ? personCells : null}
          <EntryCells entry={entry} name={name} scope={shell?.scope ?? 'global'} onChanged={onPeopleSaved} first={i === 0}>
            {i === 0 ? adamCells : null}
          </EntryCells>
        </tr>
      ))}
      {peopleReady ? <tr>{addRow(null)}</tr> : null}
    </>
  )
}

/**
 * Un rand din `it_ai_users`: firma, pasii pe intrebare si "On", cu Save si
 * scoatere. Celulele de pe om (cand e primul rand) se pun intre firma si pasi,
 * ca in capul tabelului.
 */
function EntryCells({
  entry,
  name,
  scope,
  onChanged,
  first,
  children,
}: {
  entry: AiPerson
  name: string
  scope: string
  onChanged(): void
  first: boolean
  children: ReactNode
}) {
  const [steps, setSteps] = useState(entry.max_steps)
  const [on, setOn] = useState(entry.enabled)
  const [note, setNote] = useState<Note | null>(null)
  const where = entry.company_id > 0 ? entry.company || '#' + entry.company_id : 'any company'

  const save = () => {
    setNote({ text: '…' })
    void saveAiPerson({
      user_id: entry.user_id,
      company_id: entry.company_id,
      max_steps: steps,
      /* Jetoanele pe zi nu se mai dau din ecran: pleaca inapoi cum erau. */
      daily_token_budget: entry.daily_token_budget,
      enabled: on ? 1 : 0,
      scope,
    })
      .then((r) => setNote(r.ok ? { text: 'Saved' } : { text: r.error ?? 'Failed', bad: true }))
      .catch((e: unknown) => setNote({ text: failed(e), bad: true }))
  }

  const remove = () => {
    if (!window.confirm(`Remove ${name} from ${where}?`)) return

    void removeAiPerson(entry.ID)
      .then((r) => (r.ok ? onChanged() : setNote({ text: 'Could not remove', bad: true })))
      .catch((e: unknown) => setNote({ text: failed(e), bad: true }))
  }

  return (
    <>
      <td data-testid={'company-' + entry.ID}>{entry.company_id > 0 ? where : <em>any company</em>}</td>
      {first ? children : null}
      <td>
        <input
          type="number"
          min={0}
          max={2048}
          aria-label={`Steps per question for ${name} on ${where}`}
          className="form-control input-sm"
          style={{ width: 80 }}
          placeholder="inherit"
          value={steps}
          onChange={(e) => setSteps(Number(e.target.value))}
        />
      </td>
      <td>
        <input
          type="checkbox"
          aria-label={`On for ${name} on ${where}`}
          checked={on}
          onChange={(e) => setOn(e.target.checked)}
        />
      </td>
      <td style={{ whiteSpace: 'nowrap' }}>
        <button type="button" className="btn btn-default btn-xs" aria-label={`Save ${name} on ${where}`} onClick={save}>
          Save
        </button>{' '}
        <button
          type="button"
          className="btn btn-link btn-xs"
          aria-label={`Remove ${name} from ${where}`}
          title="Remove from this company"
          onClick={remove}
        >
          ×
        </button>{' '}
        <NoteText note={note} />
      </td>
    </>
  )
}

/** Ultimul rand al omului: o firma noua pe lista lui (it_ai_users). */
function AddCompanyCells({
  person,
  name,
  shell,
  onAdded,
  children,
}: {
  person: Person
  name: string
  shell: AiAgentShell | null
  onAdded(): void
  children: ReactNode
}) {
  const taken = new Set(person.entries.map((e) => e.company_id))
  const [companyId, setCompanyId] = useState(-1)
  const [note, setNote] = useState<Note | null>(null)

  const add = () => {
    setNote({ text: '…' })
    void saveAiPerson({
      user_id: person.user_id,
      company_id: companyId,
      max_steps: 0,
      daily_token_budget: 0,
      enabled: 1,
      scope: shell?.scope ?? 'global',
    })
      .then((r) => {
        if (!r.ok) {
          setNote({ text: r.error ?? 'Failed', bad: true })
          return
        }
        setNote({ text: 'Added' })
        setCompanyId(-1)
        onAdded()
      })
      .catch((e: unknown) => setNote({ text: failed(e), bad: true }))
  }

  return (
    <>
      <td style={{ minWidth: 180 }}>
        {person.entries.length === 0 ? (
          <div className="text-muted" style={{ fontSize: 11, marginBottom: 2 }} data-testid={'no-companies-' + person.user_id}>
            no company list - by the module right
          </div>
        ) : null}
        <div style={{ display: 'flex', gap: 4 }}>
          <select
            aria-label={`Add a company for ${name}`}
            className="form-control input-sm"
            value={companyId}
            onChange={(e) => setCompanyId(Number(e.target.value))}
          >
            <option value={-1}>+ company…</option>
            {!taken.has(0) ? <option value={0}>Any company</option> : null}
            {(shell?.allCompanies ?? [])
              .filter((c) => !taken.has(c.ID))
              .map((c) => (
                <option key={c.ID} value={c.ID}>
                  {c.name}
                </option>
              ))}
          </select>
          <button
            type="button"
            className="btn btn-default btn-xs"
            aria-label={`Add company for ${name}`}
            disabled={companyId < 0}
            onClick={add}
          >
            Add
          </button>
        </div>
        <NoteText note={note} />
      </td>
      {children}
      <td colSpan={3}></td>
    </>
  )
}

type Found = { id: number; name: string; first?: string; last?: string; email: string }

/** Prenume Nume, cand cautarea trimite bucatile; altfel numele cum vine. */
function personName(p: Found): string {
  const both = `${p.first ?? ''} ${p.last ?? ''}`.trim()
  return both !== '' ? both : p.name
}

const SOURCE_LABEL: Record<'own' | 'role' | 'platform_admin', string> = {
  own: 'Own account',
  role: 'From the role',
  platform_admin: 'Platform admin',
}

/**
 * Alegerea unui om: treptele lui Adam si, optional, o firma pe lista lui
 * (ce facea "Add" in fila People veche). Cu treptele pe None nu se scrie nimic
 * la Adam - asa se pune cineva doar pe lista unei firme.
 */
function AdamPick({
  page,
  shell,
  onSaved,
  onPeopleSaved,
}: {
  page: PlatformAdminsPage
  shell: AiAgentShell | null
  onSaved(): void
  onPeopleSaved(): void
}) {
  const [term, setTerm] = useState('')
  const [found, setFound] = useState<Found[]>([])
  const [open, setOpen] = useState(false)
  const [userId, setUserId] = useState(0)
  const [access, setAccess] = useState(1)
  const [models, setModels] = useState(1)
  const [companyId, setCompanyId] = useState(-1)
  const [note, setNote] = useState<Note | null>(null)
  const timer = useRef<number | null>(null)
  const peopleReady = shell?.peopleReady ?? false

  useEffect(() => {
    if (timer.current) window.clearTimeout(timer.current)
    if (term.trim() === '' || userId > 0) {
      setFound([])
      setOpen(false)
      return
    }

    timer.current = window.setTimeout(() => {
      void searchAiPeople(term)
        .then((r) => {
          setFound(r.people)
          setOpen(r.people.length > 0)
        })
        .catch(() => setFound([]))
    }, 250)

    return () => {
      if (timer.current) window.clearTimeout(timer.current)
    }
  }, [term, userId])

  const save = () => {
    setNote({ text: '…' })
    const levels = access > 0 || models > 0
    const company = peopleReady && companyId >= 0
    const steps: Promise<{ ok: boolean; error?: string | null; warning?: string | null }>[] = []
    if (levels) steps.push(saveAdamAccess(userId, access, models))
    if (company) {
      steps.push(
        saveAiPerson({
          user_id: userId,
          company_id: companyId,
          max_steps: 0,
          daily_token_budget: 0,
          enabled: 1,
          scope: shell?.scope ?? 'global',
        })
      )
    }

    void Promise.all(steps)
      .then((all) => {
        const bad = all.find((r) => !r.ok)
        if (bad) {
          setNote({ text: bad.error ?? 'Failed', bad: true })
        } else {
          setNote({ text: 'Saved' })
          setTerm('')
          setUserId(0)
          setCompanyId(-1)
        }
        if (levels) onSaved()
        if (company) onPeopleSaved()
      })
      .catch((e: unknown) => setNote({ text: failed(e), bad: true }))
  }

  const nothing = access === 0 && models === 0 && !(peopleReady && companyId >= 0)

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 12 }}>
      <div style={{ position: 'relative', flex: '1 1 240px' }}>
        <label htmlFor="aia-adam-person" style={{ fontSize: 12, margin: 0 }}>
          Person
        </label>
        <input
          id="aia-adam-person"
          type="text"
          className="form-control input-sm"
          placeholder="type a name or e-mail"
          autoComplete="off"
          value={term}
          onChange={(e) => {
            setTerm(e.target.value)
            setUserId(0)
          }}
        />
        {open ? (
          <div
            style={{
              position: 'absolute',
              zIndex: 20,
              background: '#fff',
              border: '1px solid #d5dbe1',
              borderRadius: 6,
              maxHeight: 220,
              overflow: 'auto',
              width: '100%',
              boxShadow: '0 6px 18px rgba(0,0,0,.08)',
            }}
          >
            {found.map((p) => (
              <a
                key={p.id}
                href="#"
                style={{ display: 'block', padding: '5px 10px', fontSize: 13, color: '#1f2933' }}
                onClick={(e) => {
                  e.preventDefault()
                  setTerm(personName(p) + (p.email ? ' · ' + p.email : ''))
                  setUserId(p.id)
                  setOpen(false)
                }}
              >
                {personName(p)}
                {p.email ? ' · ' + p.email : ''}
              </a>
            ))}
          </div>
        ) : null}
      </div>
      <div style={{ width: 170 }}>
        <label style={{ fontSize: 12, margin: 0 }}>May let Adam</label>
        <LevelSelect label="Adam access" value={access} names={page.actions} onChange={setAccess} />
      </div>
      <div style={{ width: 150 }}>
        <label style={{ fontSize: 12, margin: 0 }}>Thinking depth</label>
        <LevelSelect label="Adam depth" value={models} names={page.depths} onChange={setModels} />
      </div>
      {peopleReady ? (
        <div style={{ width: 190 }}>
          <label htmlFor="aia-adam-company" style={{ fontSize: 12, margin: 0 }}>
            Company list
          </label>
          <select
            id="aia-adam-company"
            className="form-control input-sm"
            value={companyId}
            onChange={(e) => setCompanyId(Number(e.target.value))}
          >
            <option value={-1}>— not on a company list —</option>
            <option value={0}>Any company</option>
            {(shell?.allCompanies ?? []).map((c) => (
              <option key={c.ID} value={c.ID}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      <button type="button" className="btn btn-primary btn-sm" disabled={userId < 1 || nothing} onClick={save}>
        Set level
      </button>
      <NoteText note={note} />
    </div>
  )
}
